<script setup lang="ts">
import type {MessageProgress} from '@/client/mentor/MessagesClient'
import {MessageProgressLifecycle, MessageProgressPhase} from '@/client/mentor/MessagesClient'
import DebugBadge from '@/components/assistant/chat/message/DebugBadge.vue'
import {computed} from 'vue'

const props = defineProps<{
  progress?: MessageProgress[] | null
}>()

function findFirstCreatedAt(progress: MessageProgress[], lifecycle: MessageProgressLifecycle): number | null {
  return progress.find((entry) => entry.lifecycle === lifecycle)?.createdAt?.getTime?.() ?? null
}

const terminalEntry = computed(() =>
  (props.progress ?? [])
    .slice()
    .reverse()
    .find((entry) => entry.lifecycle === MessageProgressLifecycle.COMPLETED || entry.lifecycle === MessageProgressLifecycle.FAILED)
)

// duration of each lifecycle transition: message created -> pending -> running -> completed/failed
const lifecycleDurations = computed(() => {
  const progress = props.progress ?? []
  const pendingStartedAt = findFirstCreatedAt(progress, MessageProgressLifecycle.PENDING)
  const runningStartedAt = findFirstCreatedAt(progress, MessageProgressLifecycle.RUNNING)
  const terminalAt = terminalEntry.value?.createdAt?.getTime?.() ?? null

  const durations: {label: string; ms: number}[] = []
  if (pendingStartedAt != null && runningStartedAt != null) {
    durations.push({label: 'Pending → Running', ms: runningStartedAt - pendingStartedAt})
  }
  if (runningStartedAt != null && terminalAt != null) {
    const label = terminalEntry.value?.lifecycle === MessageProgressLifecycle.FAILED ? 'Running → Failed' : 'Running → Completed'
    durations.push({label, ms: terminalAt - runningStartedAt})
  }

  return durations
})

const toolProcessingMs = computed(() =>
  (props.progress ?? [])
    .filter((entry) => entry.phase === MessageProgressPhase.PROCESS_TOOL)
    .reduce((total, entry) => total + phaseDurationMs(entry), 0)
)

const totalMessageMs = computed(() => {
  const progress = props.progress ?? []
  const callPhaseIndex = progress.findIndex((entry) => entry.phase === MessageProgressPhase.CALLING_AGENT)
  const synthPhaseIndex = progress.findIndex(
    (entry, index) => index > callPhaseIndex && entry.phase === MessageProgressPhase.SYNTHESIZING_RESPONSE
  )
  if (callPhaseIndex < 0 || synthPhaseIndex < 0) return null

  const callStartedAt = progress[callPhaseIndex].createdAt?.getTime?.()
  const synthStartedAt = progress[synthPhaseIndex].createdAt?.getTime?.()
  if (callStartedAt == null || synthStartedAt == null) return null

  return synthStartedAt - callStartedAt
})

function formatSeconds(ms: number): string {
  return `${(ms / 1000).toFixed(1)}s`
}

function phaseDurationMs(entry: MessageProgress): number {
  if (entry.createdAt == null || entry.updatedAt == null) return 0
  return entry.updatedAt.getTime() - entry.createdAt.getTime()
}
</script>

<template>
  <DebugBadge v-if="totalMessageMs != null" badge-class="bg-indigo-50 text-indigo-700 border-indigo-200">
    Time: {{ formatSeconds(totalMessageMs) }}

    <template #popper>
      <p class="mb-1 text-[11px] font-bold uppercase opacity-60">Time breakdown</p>
      <div class="flex flex-col gap-0.5 text-[11px] leading-relaxed">
        <span>LLM time: {{ formatSeconds(totalMessageMs) }}</span>
        <span>Tool call wait time: {{ formatSeconds(toolProcessingMs) }}</span>
        <hr />
        <span v-for="duration in lifecycleDurations" :key="duration.label"
          >{{ duration.label }}: {{ formatSeconds(duration.ms) }}</span
        >
      </div>
    </template>
  </DebugBadge>
</template>
